import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '@/api/client';
import type { Child } from '@/api/types';
import { AppShell } from '@/components/layout';
import { Alert, Button, Card, ChildAvatar, Chip, Field, Illustration, Input, Skeleton } from '@/components/ui';
import { sceneImage } from '@/lib/illustrations';
import { useSession } from '@/hooks/session';
import { useLocale, useT } from '@/i18n';
import { ageLabel } from '@/lib/format';
import { AboutFields } from '@/features/child/AboutFields';
import { buildAbout, emptyAbout, hasAboutAnswers, type AboutState } from '@/features/child/aboutForm';
import { isoDay, validateChild } from '@/features/child/validateChild';

const GENDERS: Array<Child['gender']> = ['female', 'male', 'other', 'undisclosed'];

/** Fallback floor when `/content/config` hasn't loaded yet — 18 months, the new minimum for
 * new children (up from the old 12-month/1-year floor). */
const DEFAULT_MIN_MONTHS = 18;
const DEFAULT_MAX_YEARS = 18;

/** Reads an age floor in months back as copy: whole years read as years, anything else (the
 * common 18-month case, or an odd server-sent value) reads in months, or a mix of both. */
const monthsLabel = (months: number): string => {
  if (months % 12 === 0) return months === 12 ? '12 months' : `${months / 12} years`;
  if (months < 24) return `${months} months`;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return `${years} years ${rest} months`;
};

/** `{min}`/`{max}` vars for `child.rangeError`. */
const rangeVars = (minMonths: number, maxYears: number) => ({
  min: monthsLabel(minMonths),
  max: `${maxYears} years`,
});

export const Children = () => {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useSession();
  const navigate = useNavigate();

  const minMonths = config?.ageRange.minMonths ?? (config ? config.ageRange.minYears * 12 : DEFAULT_MIN_MONTHS);
  const maxYears = config?.ageRange.maxYears ?? DEFAULT_MAX_YEARS;

  const [children, setChildren] = useState<Child[] | null>(null);
  const [mode, setMode] = useState<'pick' | 'add'>('pick');
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [about, setAbout] = useState<AboutState>(emptyAbout);
  const [gender, setGender] = useState<Child['gender'] | null>(null);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { children: list } = await api.children.list();
        if (cancelled) return;
        setChildren(list);
        setMode(list.length > 0 ? 'pick' : 'add');
      } catch (err) {
        if (cancelled) return;
        if (!(err instanceof ApiError)) throw err;
        setChildren([]);
        setMode('add');
        setApiError(t('common.error'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const startAssessment = async (childId: string) => {
    setBusy(true);
    setApiError(null);
    try {
      const { assessment } = await api.assessments.start(childId, locale);
      navigate(`/assessments/${assessment.assessmentId}`);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      setApiError(t('common.error'));
      setBusy(false);
    }
  };

  const validation = validateChild({ name, dob, minMonths, maxYears });
  const fieldErrors = attempted && !validation.ok ? validation.errors : {};
  const showGenderError = attempted && !gender;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (!validation.ok || !gender) return;

    setBusy(true);
    setApiError(null);
    try {
      const { child } = await api.children.create({
        name: name.trim(),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        dob: dob.trim(),
        gender,
        ...(hasAboutAnswers(about) ? { about: buildAbout(about) } : {}),
      });
      await startAssessment(child.childId);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      setApiError(t('common.error'));
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto grid max-w-content items-start gap-10 nav:grid-cols-[minmax(0,560px)_1fr]">
        <div>
          <h1 className="mb-4 text-h1">{t('child.title')}</h1>

          {apiError && (
            <div className="mb-4">
              <Alert tone="warn">{apiError}</Alert>
            </div>
          )}

          {children === null ? (
            <Skeleton lines={3} />
          ) : (
            <>
              {mode === 'pick' && (
                <>
                  <ul className="mb-4 space-y-3">
                    {children.map((child) => (
                      <li key={child.childId}>
                        <Card interactive className="flex items-center gap-4">
                          <ChildAvatar name={child.name} childId={child.childId} size="lg" />
                          <span className="flex-1">
                            <span className="block text-label text-ink">{child.name}</span>
                            <span className="block text-caption text-ink-muted">
                              {ageLabel(child.ageYears, child.ageMonths)}
                            </span>
                            <Link to={`/children/${child.childId}`} className="mt-0.5 inline-flex min-h-[32px] items-center text-caption font-extrabold text-lagoon-600 hover:underline">
                              {t('child.profile')} →
                            </Link>
                          </span>
                          <Button
                            type="button"
                            disabled={busy}
                            loading={busy}
                            onClick={() => void startAssessment(child.childId)}
                          >
                            {t('child.cta')}
                          </Button>
                        </Card>
                      </li>
                    ))}
                  </ul>

                  <Button variant="secondary" onClick={() => setMode('add')}>
                    {t('child.add')}
                  </Button>
                </>
              )}

              {mode === 'add' && (
                <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      id="child-name"
                      label={t('child.name')}
                      error={fieldErrors.name ? t(fieldErrors.name) : undefined}
                    >
                      <Input
                        value={name}
                        autoComplete="given-name"
                        onChange={(e) => setName(e.target.value)}
                      />
                    </Field>
                    <Field id="child-lastname" label={t('child.lastName')}>
                      <Input
                        value={lastName}
                        maxLength={80}
                        autoComplete="family-name"
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </Field>
                  </div>

                  <Field
                    id="child-dob"
                    label={t('child.dob')}
                    error={
                      fieldErrors.dob
                        ? t(fieldErrors.dob)
                        : fieldErrors.range
                          ? t('child.rangeError', rangeVars(minMonths, maxYears))
                          : undefined
                    }
                  >
                    <Input
                      type="date"
                      value={dob}
                      max={isoDay(new Date())}
                      autoComplete="bday"
                      onChange={(e) => setDob(e.target.value)}
                    />
                  </Field>

                  <div>
                    <span id="child-gender-label" className="mb-1.5 block text-label text-ink">
                      {t('child.gender')}
                    </span>
                    <div
                      role="radiogroup"
                      aria-labelledby="child-gender-label"
                      aria-describedby={showGenderError ? 'child-gender-error' : undefined}
                      className="flex flex-wrap gap-2"
                    >
                      {GENDERS.map((value) => (
                        <Chip
                          key={value}
                          role="radio"
                          selected={gender === value}
                          onToggle={() => setGender(value)}
                        >
                          {t(`child.gender.${value}`)}
                        </Chip>
                      ))}
                    </div>
                    {showGenderError && (
                      <p id="child-gender-error" role="alert" className="mt-1 text-caption text-alert-ink">
                        {t('child.genderError')}
                      </p>
                    )}
                  </div>

                  <section aria-labelledby="child-about-title" className="grid gap-7 border-t border-sage-200 pt-6">
                    <div>
                      <h2 id="child-about-title" className="text-h2">
                        {t('about.title', { child: name.trim() || t('child.aboutFallback') })}
                      </h2>
                      <p className="mt-1 max-w-[38em] text-body text-ink-soft">{t('about.body')}</p>
                    </div>
                    <AboutFields
                      state={about}
                      onChange={(patch) => setAbout((prev) => ({ ...prev, ...patch }))}
                      childName={name.trim() || t('child.aboutFallback')}
                    />
                    <p className="text-caption text-ink-muted">{t('about.private')}</p>
                  </section>

                  <div className="flex gap-3">
                    <Button type="submit" loading={busy}>
                      {t('child.cta')}
                    </Button>
                    {children.length > 0 && (
                      <Button type="button" variant="secondary" disabled={busy} onClick={() => setMode('pick')}>
                        {t('common.cancel')}
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </>
          )}
        </div>
        <div aria-hidden="true" className="hidden h-[420px] nav:block">
          <Illustration src={sceneImage(mode === 'add' ? 'empty-children' : 'hero-floor-reading')} alt="" fit="contain" />
        </div>
      </div>
    </AppShell>
  );
};
